import { useEffect, useState } from 'react';
import * as jsonpatch from 'fast-json-patch';

import { HandlePatchDeal } from '../requests/DealRequests';
import { HandlePatchOrganization } from '../requests/OrganizationRequests';
import { HandlePatchPerson } from '../requests/PersonRequests';

const applyProps = (item, newProps) => {
    const newItem = jsonpatch.deepClone(item);

    Object.keys(newProps).forEach((propName) => {
        const oldValue = item[propName];
        const newValue = newProps[propName];
        if (newValue?.id) {
            newItem[propName + 'Id'] = newValue.id;
        } else if (newValue === null && oldValue !== undefined) {
            delete newItem[propName];
        } else {
            newItem[propName] = newValue;
        }
    });

    return newItem;
};

export const updateDealProps = async (deal, newProps) => {
    const newDeal = applyProps(deal, newProps);

    const response = await HandlePatchDeal(deal, newDeal);
    return response;
};

export const updateOrganizationProps = async (organization, newProps) => {
    const newOrganization = applyProps(organization, newProps);

    const response = await HandlePatchOrganization(organization, newOrganization);
    return response;
};

export const updatePersonProps = async (person, newProps) => {
    const newPerson = applyProps(person, newProps);

    const response = await HandlePatchPerson(person, newPerson);
    return response;
};

/**
 * Given a pipelineStageId and the pipelines in the store, this method finds the id of the pipeline
 * this stages is located in together with the index this stage has in pipeline.pipelineStages.
 * @param {pipeline} pipelines <c>store.pipelines</c>
 * @param {string} pipelineStageId
 * @returns [pipelineId, pipelineStageIndex] if found, else [undefined, -1]
 */
export const findPipelineIdPipelineStageIndex = (pipelines, pipelineStageId) => {
    const pipelineIds = Object.keys(pipelines);
    for (let k = 0; k < pipelineIds.length; k++) {
        const pipelineId = pipelineIds[k];
        const stages = pipelines[pipelineId].pipelineStages;
        for (let stageIndex = 0; stageIndex < stages.length; stageIndex++) {
            const stage = stages[stageIndex];
            if (stage.id === pipelineStageId) {
                return [pipelineId, stageIndex];
            }
        }
    }
    return [undefined, -1];
};

const setChangelogValues = (changelog, sharedStore) => {
    if (!changelog.oldValue && changelog.operation === 'add') changelog.oldText = 'Add';
    else if (!changelog.value && changelog.operation === 'remove') changelog.text = 'Remove';

    if (changelog.path == 'DealStatus') {
        changelog.text = changelog.value?.toLowerCase();
        changelog.oldText = changelog.oldValue?.toLowerCase();

        changelog.text = changelog.text === '' ? 'null' : changelog.text;
        changelog.oldText = changelog.oldText === '' ? 'null' : changelog.oldText;

        changelog.text = sharedStore.dealStatusOptions.find((elem) => elem.id == changelog.text)?.name;
        changelog.oldText = sharedStore.dealStatusOptions.find((elem) => elem.id == changelog.oldText)?.name;
    }

    //for arrays like "ChildOrganizations/0/..."
    let stringArrayCharIndex = changelog.path.indexOf('/');
    let substrigPath = changelog.path.substr(0, stringArrayCharIndex > 0 ? stringArrayCharIndex : changelog.path.length);

    changelog.title = sharedStore.propertyTitles.find((elem) => elem.id == substrigPath)?.name ?? changelog.path;

    if (changelog.text == null) changelog.text = '';
    if (changelog.oldText == null) changelog.oldText = '';

    return changelog;
};

export const CompletedHistoryFetch = (res, sharedStore) => {
    let activities = [];
    if (res['data']['activities'].length) {
        if (res['data']['activities'][0]['personId'] && res['data']['activities'][0]['activity']) {
            // Entity is ActivityPerson
            activities = res['data']['activities']
                .filter((activityPerson) => activityPerson.activity && activityPerson.activity.isActive === true)
                .map((activityPerson) => {
                    let activity = activityPerson.activity;
                    activity['activityPersonType'] = activityPerson.type;
                    return activity;
                });
        } else {
            // Entity is Activity
            activities = res['data']['activities'].filter((activity) => activity.isActive === true);
        }
    }

    let notes = res['data']['notes'];
    let files = res['data']['files'].filter((file) => file.isActive == true);
    let changelogs = res['data']['changelogs'];

    changelogs = changelogs.map((changelog) => setChangelogValues(changelog, sharedStore));

    for (let i = 0; i < activities.length; i++) {
        Object.assign(activities[i], { generalType: 'activity' });
    }
    for (let i = 0; i < notes.length; i++) {
        Object.assign(notes[i], { generalType: 'note' });
    }
    for (let i = 0; i < files.length; i++) {
        Object.assign(files[i], { generalType: 'file' });
    }
    for (let i = 0; i < changelogs.length; i++) {
        Object.assign(changelogs[i], { generalType: 'changelog' });
    }

    Object.assign(res['data'], { activities: activities, notes: notes, files: files, changelogs: changelogs });
    return res;
};

export const CalculateSettings = (viewSettings, requestObject) => {
    let viewportHeight = requestObject.amount * viewSettings.itemHeight;
    const totalHeight = (viewSettings.maxIndex - viewSettings.minIndex) * viewSettings.itemHeight;
    const toleranceHeight = viewSettings.tolerance * viewSettings.itemHeight;
    const bufferHeight = viewportHeight + 2 * toleranceHeight;
    let itemsAbove = viewSettings.startIndex - viewSettings.tolerance - viewSettings.minIndex;

    itemsAbove = itemsAbove > 0 ? itemsAbove : 0;
    const topPaddingHeight = itemsAbove * viewSettings.itemHeight;
    let bottomPaddingHeight = totalHeight - topPaddingHeight;

    // initial state object
    return {
        ...viewSettings,
        totalHeight,
        bufferHeight,
        topPaddingHeight,
        bottomPaddingHeight,
    };
};

export const CalculateDealSettings = (viewSettings, requestObject) => {
    let bottomPaddings = [];

    let viewportHeight;
    if (viewSettings.maxIndex < requestObject.amount) {
        viewportHeight = viewSettings.maxIndex * viewSettings.itemHeight;
    } else {
        viewportHeight = requestObject.amount * viewSettings.itemHeight;
    }

    let itemsAbove = requestObject.startIndex - viewSettings.tolerance - viewSettings.minIndex;
    itemsAbove = itemsAbove > 0 ? itemsAbove : 0;
    let topPaddingHeight = itemsAbove * viewSettings.itemHeight;

    let totalHeight = (viewSettings.maxIndex - viewSettings.minIndex) * viewSettings.itemHeight;

    if (viewSettings.maxIndex < requestObject.amount + 2 * viewSettings.tolerance) {
        bottomPaddings.push(0);
    } else {
        bottomPaddings.push(totalHeight - topPaddingHeight);
    }

    const bufferHeight = viewportHeight;
    const bottomPaddingHeight = Math.max(...bottomPaddings);

    // initial state object
    return {
        ...viewSettings,
        totalHeight,
        bufferHeight,
        topPaddingHeight,
        bottomPaddingHeight,
    };
};

export const Debouncer = (value, delay) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [debouncedValue, setDebouncedValue] = useState(value);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

export const IsHtml = (textValue) => {
    let htmlRegex = new RegExp('<([A-Za-z][A-Za-z0-9]*)\\b[^>]*>(.*?)</\\1>');
    return htmlRegex.test(textValue);
};
